import { useState } from "react";
import { Phone, Mail, MapPin, Building2, ShieldCheck, Truck, Sparkles, Send, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const COMPANY = {
  name: "Nicobrand S.R.L.",
  cui: "51705790",
  phone: "0750295264",
  email: "cosmindanuu@gmail.com",
  address: "Aleea Ștrandului, Tecuci, jud. Galați",
};

const values = [
  { icon: ShieldCheck, title: "Calitate garantată", desc: "Formule eficiente pentru rufe impecabile la fiecare spălare." },
  { icon: Sparkles, title: "Texturi fine și moi", desc: "Rufe catifelate și parfum de lungă durată." },
  { icon: Truck, title: "Livrare rapidă", desc: "Transport gratuit la toate comenzile, plată comodă la livrare." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Mesaj de la ${form.name || "client"} - ClarWash`);
    const body = encodeURIComponent(`${form.message}\n\nContact: ${form.name} (${form.email})`);
    window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bubble-bg noise-overlay">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center text-white sm:px-6 lg:px-8">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
            <Droplets className="h-6 w-6" />
          </span>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Despre noi</h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/90">
            ClarWash este marca de detergenți și balsam de rufe dezvoltată de {COMPANY.name}.
            Punem accent pe curățenie profesională, ingrediente eficiente și prețuri corecte — pentru rufe
            curate, parfumate și catifelate în fiecare casă.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-3">
          {values.map((v) => (
            <div key={v.title} className="rounded-xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <v.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-slate-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Contact</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">Ia legătura cu noi</h2>
            <p className="mt-3 text-slate-600">
              Ai întrebări despre produse sau comenzi? Suntem aici să te ajutăm.
            </p>

            <div className="mt-8 space-y-5">
              <ContactRow icon={Building2} label="Firmă" value={`${COMPANY.name} · CUI ${COMPANY.cui}`} testid="contact-company" />
              <ContactRow icon={Phone} label="Telefon" value={COMPANY.phone} href={`tel:${COMPANY.phone}`} testid="contact-phone" />
              <ContactRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-email" />
              <ContactRow icon={MapPin} label="Adresă" value={COMPANY.address} testid="contact-address" />
            </div>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="contact-form">
            <h3 className="font-heading text-lg font-bold text-slate-900">Trimite-ne un mesaj</h3>
            <div className="mt-5 space-y-4">
              <div>
                <Label htmlFor="c-name">Nume</Label>
                <Input id="c-name" value={form.name} onChange={set("name")} className="mt-1.5" data-testid="contact-name-input" required />
              </div>
              <div>
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="contact-email-input" required />
              </div>
              <div>
                <Label htmlFor="c-msg">Mesaj</Label>
                <Textarea id="c-msg" value={form.message} onChange={set("message")} rows={4} className="mt-1.5" data-testid="contact-message-input" required />
              </div>
              <Button type="submit" className="w-full rounded-full hover:bg-primary/90" data-testid="contact-submit">
                <Send className="mr-2 h-4 w-4" /> Trimite mesajul
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const content = (
    <div className="flex items-start gap-4" data-testid={testid}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
        <p className="font-medium text-slate-900">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} className="block transition-opacity hover:opacity-80">{content}</a> : content;
}
